import { dayStats, pluralize } from "./layout";

const BAR_ACTIONS = [
  "click->boarding-calendar#selectBar",
  "mouseenter->boarding-calendar#showCard",
  "mouseleave->boarding-calendar#hideCard",
  "mousemove->boarding-calendar#movePointer",
  "focus->boarding-calendar#showCard",
  "blur->boarding-calendar#hideCard",
].join(" ");

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined && text !== null) node.textContent = text;
  return node;
}

function icon(name, className, style = "fa") {
  const node = document.createElement("i");
  node.className = `${style} fa-${name}${className ? ` ${className}` : ""}`;
  node.setAttribute("aria-hidden", "true");
  return node;
}

export function petsFor(booking) {
  if ((booking.pets || []).length > 0) return booking.pets;
  return [{ name: booking.owner || "Booking", photo: "" }];
}

export function petsLabel(booking) {
  return petsFor(booking)
    .map((pet) => pet.name)
    .join(", ");
}

function avatar(pet, className) {
  const node = el("span", `bc-av ${className}`);
  node.title = pet.name;
  const fallback = () => {
    node.textContent = "";
    node.appendChild(icon("dog"));
  };
  if (pet.photo) {
    const image = document.createElement("img");
    image.src = pet.photo;
    image.alt = "";
    image.loading = "lazy";
    image.addEventListener("error", fallback);
    node.appendChild(image);
  } else {
    fallback();
  }
  return node;
}

function pagerChevron(name, direction, unit, margin) {
  const wrapper = el("span", "bc-bar-pager");
  wrapper.dataset.action = `click->boarding-calendar#${direction}FromBar`;
  wrapper.title = `${direction === "previous" ? "Previous" : "Next"} ${unit}`;
  if (margin) wrapper.style.marginLeft = margin;
  wrapper.appendChild(icon(name, "bc-bar-chevron"));
  return wrapper;
}

export function buildBar(bar, booking, { selected = false, unit = "week", mobile = false } = {}) {
  const link = el("a", `bc-bar${selected ? " bc-bar-selected" : ""}`);
  link.href = booking.url;
  link.dataset.bookingId = booking.id;
  link.dataset.action = BAR_ACTIONS;
  link.setAttribute("aria-label", bar.tip);
  if (mobile) {
    link.setAttribute("aria-haspopup", "dialog");
    link.setAttribute("aria-expanded", selected ? "true" : "false");
  }
  link.style.left = `${bar.left}%`;
  link.style.width = bar.width;
  link.style.top = `${bar.top}px`;
  link.style.borderRadius = bar.radius;
  link.style.background = bar.color;
  link.style.color = bar.fg;
  link.style.gap = bar.gap;
  link.style.padding = bar.padding;

  if (bar.showLeftChevron) link.appendChild(pagerChevron("chevron-left", "previous", unit));
  if (bar.showTimes) link.appendChild(el("span", "bc-bar-time", booking.in_short));

  if (bar.showAvatars) {
    const group = el("span", "bc-avs");
    petsFor(booking)
      .slice(0, bar.avatarCount)
      .forEach((pet, index) => {
        const node = avatar(pet, "bc-av-sm");
        node.style.borderColor = bar.color;
        if (index > 0) node.style.marginLeft = "-7px";
        group.appendChild(node);
      });
    if (bar.moreAvatars) group.appendChild(el("span", "bc-av-more", bar.moreAvatars));
    link.appendChild(group);
  }

  if (bar.showName) link.appendChild(el("span", "bc-bar-name", bar.name));
  if (bar.showInitial) link.appendChild(el("span", "bc-bar-initial", bar.initial));
  if (bar.showOwner) link.appendChild(el("span", "bc-bar-owner", booking.owner));

  if (bar.showTimes) {
    const out = el("span", "bc-bar-time bc-bar-time-out", booking.out_short);
    link.appendChild(out);
  }

  if (bar.showRightChevron) link.appendChild(pagerChevron("chevron-right", "next", unit, bar.chevronMargin));

  return link;
}

export function buildOutsideLabel(bar, booking, { selected = false, mobile = false } = {}) {
  const link = el("a", `bc-outside${selected ? " bc-outside-selected" : ""}`);
  link.href = booking.url;
  link.dataset.bookingId = booking.id;
  link.dataset.action = BAR_ACTIONS;
  link.setAttribute("aria-label", bar.tip);
  if (mobile) link.setAttribute("aria-haspopup", "dialog");
  link.style.left = bar.outsideLeft;
  link.style.right = bar.outsideRight;
  link.style.top = `${bar.top}px`;

  const node = avatar(petsFor(booking)[0], "bc-av-out");
  node.style.borderColor = bar.color;
  link.appendChild(node);
  link.appendChild(document.createTextNode(bar.outsideLabel));
  return link;
}

function blockIcon(block, className) {
  return block.all_day ? icon("lock", className) : icon("clock", className, "far");
}

export function buildBlockBar(bar, block, selected) {
  const button = el("button", `bc-block-bar ${block.all_day ? "bc-block-bar-allday" : "bc-block-bar-hours"}${selected ? " bc-block-bar-selected" : ""}`);
  button.type = "button";
  button.dataset.blockId = block.id;
  button.dataset.action = "click->boarding-calendar#selectBlock";
  button.title = bar.tip;
  button.setAttribute("aria-label", bar.tip);
  button.setAttribute("aria-haspopup", "dialog");
  button.setAttribute("aria-expanded", selected ? "true" : "false");
  button.style.left = bar.left;
  button.style.width = bar.width;
  button.style.top = `${bar.top}px`;
  button.style.borderRadius = bar.radius;
  button.style.justifyContent = bar.justify;
  button.style.padding = bar.padding;

  button.appendChild(blockIcon(block, "bc-block-icon"));
  if (bar.showLabel) button.appendChild(el("span", "bc-block-label", bar.label));
  return button;
}

function detailRows(rows, className) {
  const grid = el("div", className);
  rows.forEach(([label, value]) => {
    grid.appendChild(el("span", "bc-detail-label", label));
    grid.appendChild(el("span", "bc-detail-value", value));
  });
  return grid;
}

function closeButton(label, className, onClose) {
  const close = el("button", className);
  close.type = "button";
  close.setAttribute("aria-label", label);
  close.appendChild(icon("xmark"));
  close.addEventListener("click", (event) => {
    event.stopPropagation();
    onClose();
  });
  return close;
}

function bookingHead(booking, close) {
  const head = el("div", "bc-pop-head");
  const swatch = el("span", "bc-pop-swatch");
  swatch.style.background = booking.color;
  head.appendChild(swatch);

  const text = el("div", "bc-pop-heading");
  text.appendChild(el("span", "bc-pop-svc", booking.svc));

  const row = el("span", "bc-pop-pet-row");
  const avatars = el("span", "bc-avs");
  petsFor(booking)
    .slice(0, 3)
    .forEach((pet, index) => {
      const node = avatar(pet, "bc-av-card");
      node.style.boxShadow = `0 0 0 1px ${booking.color}`;
      if (index > 0) node.style.marginLeft = "-8px";
      avatars.appendChild(node);
    });
  row.appendChild(avatars);
  row.appendChild(el("span", "bc-pop-pets", petsLabel(booking)));
  text.appendChild(row);
  text.appendChild(el("span", "bc-pop-owner", booking.owner));
  head.appendChild(text);

  if (close) head.appendChild(close);
  return head;
}

function bookingDetails(booking, className) {
  return detailRows(
    [
      ["Check-in", booking.in_full],
      ["Check-out", booking.out_full],
      ["Stay", `${booking.nights} · ${booking.group}`],
    ],
    className
  );
}

function blockHeading(block, closeClass, onClose) {
  const head = el("div", "bc-pop-head");
  const tile = el("span", `bc-block-tile ${block.all_day ? "bc-block-tile-allday" : "bc-block-tile-hours"}`);
  tile.appendChild(blockIcon(block));
  head.appendChild(tile);

  const text = el("div", "bc-pop-heading");
  text.appendChild(el("span", "bc-block-eyebrow", `BLOCKED · ${block.type}`));
  text.appendChild(el("span", "bc-block-title", block.title));
  head.appendChild(text);

  head.appendChild(closeButton("Close", closeClass, onClose));
  return head;
}

function blockRows(block) {
  const grid = el("div", "bc-block-rows");
  [
    ["calendar", "far", block.when],
    ["clock", "far", block.time],
    ["layer-group", "fa", block.scope],
  ].forEach(([name, style, text]) => {
    grid.appendChild(icon(name, "bc-block-row-icon", style));
    grid.appendChild(el("span", "bc-block-row-value", text));
  });
  return grid;
}

export function buildHoverCard(booking) {
  const card = el("div", "bc-popover bc-hovercard");
  card.setAttribute("role", "tooltip");
  card.appendChild(bookingHead(booking));
  card.appendChild(bookingDetails(booking, "bc-pop-details"));
  return card;
}

export function buildBlockPopover(block, { onClose }) {
  const popover = el("div", "bc-popover");
  popover.setAttribute("role", "dialog");
  popover.setAttribute("aria-label", `${block.title} blocked time`);
  popover.appendChild(blockHeading(block, "bc-pop-close", onClose));
  popover.appendChild(blockRows(block));

  const actions = el("div", "bc-pop-actions");
  const edit = el("a", "bc-pop-btn");
  edit.href = block.edit_url;
  edit.appendChild(icon("pen"));
  edit.appendChild(document.createTextNode("Edit"));
  actions.appendChild(edit);

  const remove = el("a", "bc-pop-btn bc-pop-btn-danger");
  remove.href = block.delete_url;
  remove.rel = "nofollow";
  remove.dataset.method = "delete";
  remove.dataset.confirm = "Are you sure you want to delete this Blocked Time?";
  remove.appendChild(icon("trash-can", "", "far"));
  remove.appendChild(document.createTextNode("Remove"));
  actions.appendChild(remove);

  popover.appendChild(actions);
  return popover;
}

export function buildSheet(booking, { onClose }) {
  const sheet = el("div", "bc-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", `${petsLabel(booking)} booking`);
  sheet.appendChild(bookingHead(booking, closeButton("Close", "bc-sheet-close", onClose)));
  sheet.appendChild(bookingDetails(booking, "bc-sheet-details"));
  const open = el("a", "bc-sheet-open", "Open booking");
  open.href = booking.url;
  sheet.appendChild(open);
  return sheet;
}

export function buildBlockSheet(block, { onClose }) {
  const sheet = el("div", "bc-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", `${block.title} blocked time`);
  sheet.appendChild(blockHeading(block, "bc-sheet-close", onClose));
  sheet.appendChild(
    detailRows(
      [
        ["When", block.when],
        ["Time", block.time],
        ["Applies to", block.scope],
      ],
      "bc-sheet-details"
    )
  );
  const edit = el("a", "bc-sheet-edit", "Edit blocked time");
  edit.href = block.edit_url;
  sheet.appendChild(edit);
  return sheet;
}

function card(booking, dayIndex, compact) {
  const link = el("a", `bc-card${compact ? " bc-card-compact" : ""}`);
  link.href = booking.url;
  link.style.borderColor = booking.color;

  const top = el("div", "bc-card-top");
  const who = el("span", "bc-card-who");
  const avs = el("span", "bc-avs");
  petsFor(booking)
    .slice(0, 3)
    .forEach((pet, index) => {
      const node = avatar(pet, compact ? "bc-av-card-sm" : "bc-av-card");
      node.style.boxShadow = `0 0 0 1px ${booking.color}`;
      if (index > 0) node.style.marginLeft = compact ? "-8px" : "-8px";
      avs.appendChild(node);
    });
  who.appendChild(avs);
  who.appendChild(el("span", "bc-card-pets", petsLabel(booking)));
  top.appendChild(who);

  const arriving = Math.floor(booking.s) === dayIndex;
  const departing = Math.floor(booking.e) === dayIndex;
  if (arriving || departing) {
    const tag = el("span", "bc-card-tag", arriving ? "ARRIVING" : "DEPARTING");
    tag.style.background = booking.color;
    tag.style.color = booking.fg;
    top.appendChild(tag);
  }
  link.appendChild(top);
  link.appendChild(el("span", "bc-card-owner", booking.owner));
  link.appendChild(bookingDetails(booking, "bc-card-details"));
  link.appendChild(el("span", "bc-card-svc", booking.svc));
  return link;
}

export function buildDrillDown({ groupName, dayLabel, bookings, dayIndex, compact, onClose }) {
  const stats = dayStats(bookings, dayIndex);
  const statsText = `${pluralize(stats.dogs, "dog")} · ${stats.arriving} arriving · ${stats.departing} departing`;
  const title = `${groupName} · ${dayLabel}`;
  const panel = el("div", `bc-drill-panel${compact ? " bc-drill-panel-compact" : ""}`);

  if (compact) {
    panel.appendChild(el("span", "bc-drill-line", `${title} · ${statsText}`));
  } else {
    const head = el("div", "bc-drill-head");
    head.appendChild(el("span", "bc-drill-title", title));
    head.appendChild(el("span", "bc-drill-stats", statsText));
    const close = el("button", "bc-drill-close");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    close.appendChild(icon("xmark"));
    close.addEventListener("click", onClose);
    head.appendChild(close);
    panel.appendChild(head);
  }

  const cards = el("div", "bc-drill-cards");
  stats.list.forEach((booking) => cards.appendChild(card(booking, dayIndex, compact)));
  if (stats.list.length === 0) cards.appendChild(el("span", "bc-drill-empty", "No dogs on site"));
  panel.appendChild(cards);
  return panel;
}
